// src/components/support/Notification.jsx
import React, {useMemo, useState} from "react";
import {
    Button,
    Card,
    Col,
    Empty,
    Form,
    Grid,
    Input,
    List,
    Modal,
    Pagination,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {
    BellOutlined,
    CheckCircleOutlined,
    DeleteOutlined,
    DownOutlined,
    EyeOutlined,
    FilterOutlined,
    InfoCircleOutlined,
    ReloadOutlined,
    UpOutlined,
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {channelMeta} from "../../../utils/channel";

import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";

import {
    useBulkMarkNotificationsAsRead,
    useDeleteNotification,
    useMarkNotificationAsRead,
    useNotifications,
} from "./notification-query";
import {useSelfProfile} from "../header-query";

const {Text} = Typography;
const {useBreakpoint} = Grid;

/* ---------------- Time & format helpers ---------------- */
const TEHRAN_OFFSET_MINUTES = 210; // UTC+03:30

const toISOInstant = (v) => {
    if (!v) return null;
    const g = new DateObject(v).convert(gregorian);
    const y = g.year;
    const M = g.month.number;
    const d = g.day;
    const h = g.hour || 0;
    const m = g.minute || 0;
    const s = g.second || 0;
    const ms = g.millisecond || 0;

    const utcMs =
        Date.UTC(y, M - 1, d, h, m, s, ms) - TEHRAN_OFFSET_MINUTES * 60 * 1000;
    return new Date(utcMs).toISOString();
};

const startOfTehranDay = (v) =>
    new DateObject(v).set({hour: 0, minute: 0, second: 0, millisecond: 0});
const endOfTehranDay = (v) =>
    new DateObject(v).set({hour: 23, minute: 59, second: 59, millisecond: 999});

const rangeToInstants = (range) => {
    const [a, b] = range || [];
    const start = a ? startOfTehranDay(a) : null;
    const end = b ? endOfTehranDay(b) : null;
    return {from: toISOInstant(start), to: toISOInstant(end)};
};

const orderRange = (r) => {
    if (!r || !r[0] || !r[1]) return r;
    const aMs = r[0].toDate()?.getTime?.() ?? 0;
    const bMs = r[1].toDate()?.getTime?.() ?? 0;
    return aMs <= bMs ? r : [r[1], r[0]];
};

const toPersianDateObject = (d) =>
    d ? new DateObject({date: new Date(d), calendar: persian, locale: persian_fa}) : null;

const fmtPersian = (d, token = "HH:mm YYYY/MM/DD") => {
    const obj = toPersianDateObject(d);
    return obj ? obj.format(token) : "-";
};

const enToFaDigits = (v) =>
    String(v ?? "").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

/* ==================== MAIN COMPONENT ==================== */

const Notification = () => {
    const {t} = useTranslation();
    const screens = useBreakpoint();
    const isDesktop = !!screens.md;

    const [filterBoxShow, setFilterBoxShow] = useState(false);
    const [pagination, setPagination] = useState({current: 1, pageSize: 10});

    const [createdRange, setCreatedRange] = useState([null, null]);

    const [filters, setFilters] = useState({
        read: "ALL",
    });

    const [form] = Form.useForm();

    const createdInstants = useMemo(
        () => rangeToInstants(createdRange),
        [createdRange]
    );

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailRecord, setDetailRecord] = useState(null);

    const {mutate: markAsRead} = useMarkNotificationAsRead();
    const {mutate: bulkMarkRead} = useBulkMarkNotificationsAsRead();
    const {mutate: removeNotification} = useDeleteNotification();

    // فیلترهای ارسالی به بک‌اند
    const apiFilters = useMemo(
        () => ({
            operatorId: null,
            read:
                filters.read === "ALL"
                    ? null
                    : filters.read === "READ"
                        ? true
                        : filters.read === "UNREAD"
                            ? false
                            : null,
            type: filters.type || null,
            createdFrom: createdInstants.from || null,
            createdTo: createdInstants.to || null,
            title: filters.title || null,
            message: filters.message || null,
        }),
        [filters, createdInstants]
    );

    const {data, isLoading, refetch} = useNotifications(
        pagination.current - 1,
        pagination.pageSize,
        apiFilters
    );

    const rows = useMemo(
        () => data?.data?.content || data?.content || [],
        [data]
    );

    const total =
        data?.data?.totalElements ??
        data?.totalElements ??
        rows.length;

    const unreadCount = useMemo(
        () => rows.filter((n) => !n.read).length,
        [rows]
    );

    const openDetails = (row) => {
        setDetailRecord(row);
        setDetailOpen(true);
    };

    const handleMarkAsRead = (row) => {
        if (!row?.id || row.read) return;
        markAsRead(row.id);
    };

    const handleBulkMarkAsRead = () => {
        if (!unreadCount) return;
        bulkMarkRead();
    };

    const handleDelete = (row) => {
        Modal.confirm({
            title: t("notif.delete_title", "حذف اعلان"),
            content: t("notif.delete_confirm", "آیا از حذف این اعلان مطمئن هستید؟"),
            okText: t("notif.delete_ok", "بله، حذف شود"),
            cancelText: t("notif.delete_cancel", "انصراف"),
            onOk: () => {
                if (row?.id) {
                    removeNotification(row.id);
                }
            },
        });
    };

    const notificationTypeLabel = (type, t) => {
        switch (type) {
            case "CHAT":
                return t("notif.type.chat", "چت");
            case "MESSAGE":
                return t("notif.type.message", "پیام");
            default:
                return type || "-";
        }
    };

    /* --------- columns (desktop) --------- */
    const columns = useMemo(
        () => [
            {
                title: t("notif.col_index", "ردیف"),
                key: "index",
                align: "center",
                width: 64,
                render: (_text, _rec, idx) =>
                    enToFaDigits(
                        (pagination.current - 1) * pagination.pageSize + idx + 1
                    ),
            },
            {
                title: t("notif.col_title", "عنوان"),
                dataIndex: "title",
                key: "title",
                width: 220,
                ellipsis: {showTitle: false},
                render: (v) => (
                    <Tooltip title={v || ""}>
                        <span>{v || "-"}</span>
                    </Tooltip>
                ),
            },
            {
                // Where the person writing is actually writing FROM. An operator triaging this list
                // needs it before they open anything: a website visitor is very likely still sitting
                // on the page waiting, while a Telegram contact is not.
                title: t("notif.col_channel", "منبع"),
                dataIndex: "channel",
                key: "channel",
                align: "center",
                width: 140,
                render: (v) => {
                    const meta = channelMeta(v, t);
                    return (
                        <Tag color={meta.color} style={{borderRadius: 999}}>
                            {meta.icon} {meta.label}
                        </Tag>
                    );
                },
            },
            {
                title: t("notif.col_message", "متن اعلان"),
                dataIndex: "message",
                key: "message",
                width: 260,
                ellipsis: {showTitle: false},
                render: (v) => (
                    <Tooltip title={v || ""}>
                        <span>{v || "-"}</span>
                    </Tooltip>
                ),
            },
            {
                title: t("notif.col_type", "نوع"),
                dataIndex: "type",
                key: "type",
                align: "center",
                width: 110,
                render: (v) =>
                    v ? (
                        <Tag color={v === "CHAT" ? "blue" : "green"}>
                            {notificationTypeLabel(v, t)}
                        </Tag>
                    ) : (
                        "-"
                    ),
            },
            {
                title: t("notif.col_operator", "اپراتور"),
                dataIndex: "operatorName",
                key: "operatorName",
                align: "center",
                width: 160,
                render: (v) => v || "-",
            },
            {
                title: t("notif.col_readAt", "زمان خوانده شدن"),
                dataIndex: "readAt",
                key: "readAt",
                align: "center",
                width: 170,
                render: (v) => (v ? fmtPersian(v, "HH:mm YYYY/MM/DD") : "-"),
            },
            {
                title: t("notif.col_read", "وضعیت خواندن"),
                dataIndex: "read",
                key: "read",
                align: "center",
                width: 130,
                render: (v) =>
                    v ? (
                        <Tag color="success">{t("notif.read", "خوانده شده")}</Tag>
                    ) : (
                        <Tag color="red">{t("notif.unread", "خوانده نشده")}</Tag>
                    ),
            },
            {
                title: t("notif.col_createdAt", "تاریخ ایجاد"),
                dataIndex: "createdAt",
                key: "createdAt",
                align: "center",
                width: 170,
                render: (v) => fmtPersian(v, "HH:mm YYYY/MM/DD"),
            },
            {
                title: t("notif.actions", "عملیات"),
                key: "actions",
                fixed: "right",
                align: "center",
                width: 190,
                render: (_text, r) => (
                    <Space>
                        <Tooltip title={t("notif.mark_read", "علامت به عنوان خوانده شده")}>
                            <Button
                                size="small"
                                type="primary"
                                icon={<CheckCircleOutlined/>}
                                onClick={() => handleMarkAsRead(r)}
                                disabled={!!r.read}
                            />
                        </Tooltip>
                        <Tooltip title={t("notif.details", "جزئیات")}>
                            <Button
                                size="small"
                                icon={<EyeOutlined/>}
                                onClick={() => openDetails(r)}
                            />
                        </Tooltip>
                        <Tooltip title={t("notif.delete", "حذف")}>
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined/>}
                                onClick={() => handleDelete(r)}
                            />
                        </Tooltip>
                    </Space>
                ),
            },
        ],
        [t, pagination.current, pagination.pageSize]
    );

    /* --------- Mobile card ---------- */
    const renderMobileCard = (r, idxInPage) => (
        <Card
            key={r.id}
            size="small"
            className="notif-mobile-card"
            bodyStyle={{padding: 12}}
            onDoubleClick={() => openDetails(r)}
        >
            <div style={{display: "flex", justifyContent: "space-between", gap: 8}}>
                <Space>
                    <BellOutlined/>
                    <Text strong>{r.title || "-"}</Text>
                </Space>
                {r.read ? (
                    <Tag color="success">{t("notif.read", "خوانده شده")}</Tag>
                ) : (
                    <Tag color="red">{t("notif.unread", "خوانده نشده")}</Tag>
                )}
            </div>

            {/* Desktop has a منبع column; without this a mobile operator cannot tell a waiting
                website visitor from a Telegram contact. */}
            <div style={{marginTop: 6}}>
                <Tag color={channelMeta(r.channel, t).color} style={{borderRadius: 999}}>
                    {channelMeta(r.channel, t).icon} {channelMeta(r.channel, t).label}
                </Tag>
            </div>

            <div style={{marginTop: 6}}>
                <Text type="secondary" style={{fontSize: 12}}>
                    {t("notif.col_index", "ردیف")}:
                </Text>
                <Text style={{marginRight: 4}}>
                    {enToFaDigits(
                        (pagination.current - 1) * pagination.pageSize + idxInPage + 1
                    )}
                </Text>
            </div>

            <div style={{marginTop: 4}}>
                <Text type="secondary" style={{fontSize: 12}}>
                    {t("notif.col_message", "متن اعلان")}
                </Text>
                <div style={{fontSize: 13}}>{r.message || "-"}</div>
            </div>

            <div style={{marginTop: 4, display: "flex", justifyContent: "space-between"}}>
                <Text type="secondary" style={{fontSize: 12}}>
                    {t("notif.col_operator", "اپراتور")}
                </Text>
                <Text>{r.operatorName || "-"}</Text>
            </div>

            <div style={{marginTop: 4, display: "flex", justifyContent: "space-between"}}>
                <Text type="secondary" style={{fontSize: 12}}>
                    {t("notif.col_createdAt", "تاریخ ایجاد")}
                </Text>
                <Text>{fmtPersian(r.createdAt, "HH:mm YYYY/MM/DD")}</Text>
            </div>

            <Space style={{marginTop: 8}} wrap>
                <Button
                    size="small"
                    type="primary"
                    icon={<CheckCircleOutlined/>}
                    onClick={() => handleMarkAsRead(r)}
                    disabled={!!r.read}
                >
                    {t("notif.mark_read", "خوانده‌شده")}
                </Button>
                <Button
                    size="small"
                    icon={<EyeOutlined/>}
                    onClick={() => openDetails(r)}
                >
                    {t("notif.details", "جزئیات")}
                </Button>
                <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined/>}
                    onClick={() => handleDelete(r)}
                >
                    {t("notif.delete", "حذف")}
                </Button>
            </Space>
        </Card>
    );

    return (
        <>
            <Card
                title={
                    isDesktop ? (
                        <Space>
                            <BellOutlined/>
                            <span>{t("notif.title", "اعلان‌ها")}</span>
                        </Space>
                    ) : null
                }
                extra={
                    <Space size="small" wrap>
                        {/* bulk mark as read */}
                        <Tooltip
                            title={
                                unreadCount
                                    ? t("notif.bulk_mark_read", "علامت‌گذاری همه به عنوان خوانده شده")
                                    : t("notif.no_unread", "اعلان خوانده‌نشده‌ای وجود ندارد")
                            }
                        >
                            <Button
                                type="primary"
                                icon={<CheckCircleOutlined/>}
                                disabled={!unreadCount}
                                onClick={handleBulkMarkAsRead}
                            >
                                {t("notif.bulk_mark_read_btn", "همه خوانده شد")}
                            </Button>
                        </Tooltip>

                        <Tooltip title={t("notif.filter", "فیلتر")}>
                            <Button
                                icon={filterBoxShow ? <UpOutlined/> : <DownOutlined/>}
                                onClick={() => setFilterBoxShow((s) => !s)}
                            >
                                {t("notif.filter", "فیلتر")}
                            </Button>
                        </Tooltip>
                        <Tooltip title={t("notif.reload", "بارگذاری مجدد")}>
                            <Button
                                icon={<ReloadOutlined/>}
                                onClick={() => {
                                    refetch();
                                }}
                            >
                                {t("notif.reload", "بارگذاری مجدد")}
                            </Button>
                        </Tooltip>
                    </Space>
                }
                bodyStyle={{padding: 12}}
            >
                {/* فیلترها */}
                {filterBoxShow && (
                    <Card size="small" style={{marginBottom: 12}}>
                        <Form
                            layout="vertical"
                            form={form}
                            onFinish={(vals) => {
                                setFilters({
                                    operatorId: vals.operatorId || null,
                                    read: vals.read || "ALL",
                                    type: vals.type || null,
                                    title: vals.title || null,
                                    message: vals.message || null,
                                });
                                setPagination((prev) => ({
                                    ...prev,
                                    current: 1,
                                }));
                            }}
                        >
                            <Row gutter={[16, 8]}>
                                <Col xs={24} sm={12} md={4}>
                                    <Form.Item
                                        name="read"
                                        label={t("notif.filter_read", "وضعیت خواندن")}
                                        initialValue="ALL"
                                    >
                                        <Select
                                            allowClear
                                            options={[
                                                {value: "ALL", label: t("notif.all", "همه")},
                                                {
                                                    value: "UNREAD",
                                                    label: t("notif.unread", "خوانده نشده"),
                                                },
                                                {
                                                    value: "READ",
                                                    label: t("notif.read", "خوانده شده"),
                                                },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={4}>
                                    <Form.Item
                                        name="type"
                                        label={t("notif.filter_type", "نوع")}
                                    >
                                        <Select
                                            allowClear
                                            placeholder={t(
                                                "notif.filter_type_placeholder",
                                                "نوع اعلان"
                                            )}
                                            options={[
                                                {value: "CHAT", label: "چت"},
                                                {value: "MESSAGE", label: "پیام"},
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={5}>
                                    <Form.Item
                                        name="title"
                                        label={t("notif.filter_title", "عنوان")}
                                    >
                                        <Input
                                            placeholder={t(
                                                "notif.filter_title_placeholder",
                                                "جستجو در عنوان"
                                            )}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={5}>
                                    <Form.Item
                                        name="message"
                                        label={t("notif.filter_message", "متن اعلان")}
                                    >
                                        <Input
                                            placeholder={t(
                                                "notif.filter_message_placeholder",
                                                "جستجو در متن اعلان"
                                            )}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        label={t(
                                            "notif.created_range",
                                            "بازه تاریخ ایجاد اعلان"
                                        )}
                                    >
                                        <DatePicker
                                            range
                                            value={createdRange}
                                            onChange={(v) => setCreatedRange(orderRange(v))}
                                            calendar={persian}
                                            locale={persian_fa}
                                            format="YYYY/MM/DD"
                                            style={{width: "100%"}}
                                            calendarPosition="bottom-right"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row justify="center">
                                <Col>
                                    <Space>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            icon={<FilterOutlined/>}
                                        >
                                            {t("notif.filter", "فیلتر")}
                                        </Button>
                                        <Button
                                            icon={<ReloadOutlined/>}
                                            onClick={() => {
                                                form.resetFields();
                                                setCreatedRange([null, null]);
                                                setFilters({read: "ALL"});
                                                setPagination({current: 1, pageSize: 10});
                                            }}
                                        >
                                            {t("notif.reset", "ریست")}
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                )}

                {/* جدول دسکتاپ / کارت موبایل */}
                {isDesktop ? (
                    <Table
                        rowKey="id"
                        size="small"
                        bordered
                        dataSource={rows}
                        columns={columns}
                        loading={isLoading}
                        pagination={false}
                        locale={{
                            emptyText: (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={t("notif.no_data", "اعلانی یافت نشد")}
                                />
                            ),
                        }}
                        scroll={{x: 1100}}
                        rowClassName={(_, idx) => (idx % 2 === 0 ? "notif-zebra-row" : "")}
                        onRow={(record) => ({
                            onDoubleClick: () => openDetails(record),
                        })}
                    />
                ) : (
                    <List
                        dataSource={rows}
                        split={false}
                        className="notif-mobile-list"
                        locale={{emptyText: t("notif.no_data", "اعلانی یافت نشد")}}
                        renderItem={(item, idx) => (
                            <List.Item style={{padding: 0, border: "none"}}>
                                {renderMobileCard(item, idx)}
                            </List.Item>
                        )}
                        style={{marginBottom: 12}}
                    />
                )}

                {/* pagination */}
                {total > 0 && (
                    <div
                        style={{
                            marginTop: 12,
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Pagination
                            current={pagination.current}
                            pageSize={pagination.pageSize}
                            total={total}
                            showSizeChanger
                            pageSizeOptions={["5", "10", "20", "50"]}
                            locale={{items_per_page: ""}}
                            onChange={(current, pageSize) =>
                                setPagination({current, pageSize})
                            }
                        />
                    </div>
                )}

                <style>{`
          .notif-zebra-row td { background: var(--npe-row-alt); }

          @media (max-width: 767.99px) {
            .notif-mobile-list .ant-list-items {
              display: flex;
              flex-direction: column;
              gap: 10px;
            }
            .notif-mobile-card {
              width: 100%;
              border-radius: 10px;
            }
          }
        `}</style>
            </Card>

            {/* جزئیات اعلان */}
            <Modal
                open={detailOpen}
                onCancel={() => setDetailOpen(false)}
                onOk={() => setDetailOpen(false)}
                title={
                    <Space>
                        <InfoCircleOutlined/>
                        <span>{t("notif.details_title", "جزئیات اعلان")}</span>
                    </Space>
                }
                okText={t("notif.close", "بستن")}
                cancelButtonProps={{style: {display: "none"}}}
                width={720}
            >
                {detailRecord && (
                    <div style={{display: "grid", rowGap: 8}}>
                        <Space>
                            <BellOutlined/>
                            <Text strong>{detailRecord.title || "-"}</Text>
                        </Space>

                        <div>
                            <Text type="secondary">
                                {t("notif.col_operator", "اپراتور")}:{" "}
                            </Text>
                            <Text>{detailRecord.operatorName || "-"}</Text>
                        </div>

                        <div>
                            <Text type="secondary">
                                {t("notif.col_type", "نوع")}:{" "}
                            </Text>
                            <Text>{notificationTypeLabel(detailRecord.type, t)}</Text>
                        </div>

                        <div>
                            <Text type="secondary">
                                {t("notif.col_createdAt", "تاریخ ایجاد")}:{" "}
                            </Text>
                            <Text>{fmtPersian(detailRecord.createdAt, "HH:mm YYYY/MM/DD")}</Text>
                        </div>

                        <div>
                            <Text type="secondary">
                                {t("notif.col_read", "وضعیت خواندن")}:{" "}
                            </Text>
                            {detailRecord.read ? (
                                <Tag color="success">{t("notif.read", "خوانده شده")}</Tag>
                            ) : (
                                <Tag color="red">{t("notif.unread", "خوانده نشده")}</Tag>
                            )}
                        </div>

                        {detailRecord.readAt && (
                            <div style={{marginTop: 4}}>
                                <Text type="secondary">{t("notif.col_readAt", "زمان خوانده شدن")}</Text>
                                <Text>{fmtPersian(detailRecord.readAt, "HH:mm YYYY/MM/DD")}</Text>
                            </div>
                        )}

                        <div>
                            <Text type="secondary">
                                {t("notif.col_message", "متن اعلان")}:{" "}
                            </Text>
                            <div>{detailRecord.message || "-"}</div>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default Notification;
